import React from 'react';
import { Phone, Mail, MapPin, Award, Lightbulb } from 'lucide-react';

const Brochure = () => {
  return (
    <div className="brochure-container bg-white">
      {/* Page 1: Cover */}
      <div className="brochure-page cover-page bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100">
        <div className="flex flex-col items-center justify-center h-full px-16">
          <div className="text-center mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/yv3r7xql_Adobe%20Express%20-%20file.png"
              alt="Camel Logo"
              className="h-32 w-32 mx-auto mb-6"
              style={{
                filter: 'brightness(0) saturate(100%) invert(29%) sepia(74%) saturate(1447%) hue-rotate(2deg) brightness(94%) contrast(97%)'
              }}
            />
            <h1 className="text-6xl font-bold text-amber-800 mb-4" style={{ fontFamily: "'Monotype Corsiva', cursive" }}>
              Camel
            </h1>
            <p className="text-2xl text-amber-700 font-semibold mb-8">Rubber Industries</p>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">
              Premium Rubber Bush Solutions
            </h2>
            <p className="text-xl text-amber-700 font-semibold mb-2">
              धूप में ढलता नहीं, रेत में रुकता नहीं
            </p>
            <p className="text-lg text-gray-600 italic">
              "Unyielding in Heat, Unstoppable in Sand"
            </p>
          </div>

          <div className="w-full max-w-2xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/ry9g7xa5_Gemini_Generated_Image_k5gzztk5gzztk5gz.png"
              alt="Camel Rubber Bush"
              className="w-full h-64 object-cover rounded-2xl shadow-2xl"
            />
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-sm">Est. 2012 | 40+ Years of Industry Expertise</p>
          </div>
        </div>
      </div>

      {/* Page 2-3: About & Why Choose Us */}
      <div className="brochure-page">
        <div className="grid grid-cols-2 gap-8 h-full p-16">
          {/* Left Column */}
          <div className="space-y-6">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                About <span className="text-amber-700">Camel Rubber Industries</span>
              </h2>
              <div className="w-24 h-1 bg-amber-600 mb-6"></div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Premium quality rubber bushes designed for submersible pumps that face the toughest conditions. 
                Established in 2012, Camel Rubber Industries has been at the forefront of manufacturing high-performance 
                rubber components for industrial applications.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our products are engineered to withstand extreme heat and sand erosion, significantly extending pump 
                life and reducing maintenance costs in challenging environments.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border-2 border-amber-200">
              <h3 className="text-xl font-bold text-amber-800 mb-3">Our Mission</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                To provide innovative, high-quality rubber bush solutions that enhance the performance and longevity 
                of submersible pumps in the most demanding conditions.
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-800 mb-6">
              Why Choose Camel?
            </h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Just as a camel stands resilient in the sandy desert, our Camel Rubber Bush is engineered to endure 
              the harshest conditions. When submersible pumps draw water, they inevitably pull sand and debris along 
              with it. This constant abrasion destroys ordinary rubber bushes quickly.
            </p>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200 text-center">
                <div className="text-base font-bold text-amber-700 leading-snug">Field<br/>Tested</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200 text-center">
                <div className="text-base font-bold text-amber-700 leading-snug">Premium<br/>Quality</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200 text-center">
                <div className="text-base font-bold text-amber-700 leading-snug">Multiple<br/>Sizes</div>
              </div>
            </div>

            <div className="mt-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/e0vjte0q_Gemini_Generated_Image_4grzti4grzti4grz.png"
                alt="Rubber Bush"
                className="w-full h-48 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Page 4-5: Meet the Founder */}
      <div className="brochure-page bg-gradient-to-b from-white to-amber-50">
        <div className="h-full p-16">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-3">
            Meet the <span className="text-amber-700">Founder</span>
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-12"></div>

          <div className="grid grid-cols-5 gap-12 items-center">
            {/* Photo Column */}
            <div className="col-span-2 flex flex-col items-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-amber-600 shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/n02yfgo3_Gemini_Generated_Image_enxbruenxbruenxb.png"
                    alt="Hasmukh Patel"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-2 rounded-full shadow-lg">
                  <div className="flex items-center space-x-2">
                    <Award size={16} />
                    <span className="font-bold text-sm">40+ Years Experience</span>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <h3 className="text-3xl font-bold text-gray-800 mb-2">Hasmukh Patel</h3>
                <p className="text-lg text-amber-700 font-semibold mb-1">Founder & Owner</p>
                <p className="text-sm text-gray-600">Leading Camel Rubber Industries since 2012</p>
              </div>
            </div>

            {/* Content Column */}
            <div className="col-span-3 space-y-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                With over four decades of industry expertise, Hasmukh Patel stands as a visionary leader 
                in rubber bush manufacturing. A true innovator, he has personally designed cutting-edge 
                machinery that revolutionizes productivity and efficiency in the field.
              </p>
              
              <p className="text-gray-700 leading-relaxed text-lg">
                As a one-man army with unparalleled knowledge and experience, he brings extensive 
                understanding of every aspect of the product. His unwavering commitment to quality, 
                customer satisfaction, and unique problem-solving solutions has established Camel 
                Rubber Industries as a trusted name in the industry.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-6">
                <div className="bg-white p-5 rounded-xl shadow-lg border border-amber-200">
                  <Award className="text-amber-600 mb-3" size={32} />
                  <p className="font-bold text-gray-800 text-lg mb-1">Quality Driven</p>
                  <p className="text-sm text-gray-600">Committed to premium products and customer satisfaction</p>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-lg border border-amber-200">
                  <Lightbulb className="text-amber-600 mb-3" size={32} />
                  <p className="font-bold text-gray-800 text-lg mb-1">Innovation Expert</p>
                  <p className="text-sm text-gray-600">Designing custom solutions and advanced machinery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 6-7: Nitrile Rubber Bush */}
      <div className="brochure-page">
        <div className="h-full p-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            Metal Cover <span className="text-amber-700">Nitrile Rubber Bush</span>
          </h2>
          <div className="w-24 h-1 bg-amber-600 mb-8"></div>

          <div className="grid grid-cols-2 gap-12 h-4/5">
            {/* Left: Product Images */}
            <div className="space-y-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/ry9g7xa5_Gemini_Generated_Image_k5gzztk5gzztk5gz.png"
                alt="Nitrile Rubber Bush"
                className="w-full h-96 object-cover rounded-xl shadow-xl"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/uwx09ueh_Gemini_Generated_Image_snhk5csnhk5csnhk.png"
                alt="Nitrile Rubber Bush Detail"
                className="w-full h-64 object-cover rounded-xl shadow-xl"
              />
            </div>

            {/* Right: Product Description */}
            <div className="flex flex-col justify-center space-y-8">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl border-2 border-amber-200">
                <h3 className="text-2xl font-bold text-amber-800 mb-6">Product Overview</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our Metal Cover Nitrile Rubber Bush is specifically designed for submersible pumps operating 
                  in challenging environments. Engineered with premium nitrile rubber compound and reinforced 
                  with a durable metal cover for maximum protection.
                </p>
                
                <h3 className="text-2xl font-bold text-amber-800 mb-4 mt-8">Key Features</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Superior resistance to heat and sand erosion</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Metal cover for enhanced durability and protection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Extended pump life and reduced maintenance costs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Ideal for sandy and high-temperature environments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Available in multiple sizes for various applications</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 8-9: PU Cast Rubber Bush */}
      <div className="brochure-page">
        <div className="h-full p-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            Metal Cover <span className="text-amber-700">PU Cast Rubber Bush</span>
          </h2>
          <div className="w-24 h-1 bg-amber-600 mb-8"></div>

          <div className="grid grid-cols-2 gap-12 h-4/5">
            {/* Left: Product Images */}
            <div className="space-y-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/j535ejmu_Gemini_Generated_Image_uqblw2uqblw2uqbl.png"
                alt="PU Cast Rubber Bush"
                className="w-full h-96 object-cover rounded-xl shadow-xl"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/e0vjte0q_Gemini_Generated_Image_4grzti4grzti4grz.png"
                alt="PU Cast Rubber Bush Detail"
                className="w-full h-64 object-cover rounded-xl shadow-xl"
              />
            </div>

            {/* Right: Product Description */}
            <div className="flex flex-col justify-center space-y-8">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl border-2 border-amber-200">
                <h3 className="text-2xl font-bold text-amber-800 mb-6">Product Overview</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our Metal Cover PU Cast Rubber Bush features premium polyurethane casting for exceptional 
                  performance in demanding applications. The advanced PU formulation provides superior abrasion 
                  resistance and longer service life compared to conventional rubber materials.
                </p>
                
                <h3 className="text-2xl font-bold text-amber-800 mb-4 mt-8">Key Features</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Premium polyurethane (PU) cast material</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Exceptional abrasion and tear resistance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Superior load-bearing capacity</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Longer service life in demanding applications</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-3 text-xl">•</span>
                    <span>Available in multiple sizes for various needs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 10-11: Product Gallery */}
      <div className="brochure-page bg-gradient-to-b from-white to-amber-50">
        <div className="h-full p-16">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-3">
            Product <span className="text-amber-700">Gallery</span>
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-12"></div>

          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/ry9g7xa5_Gemini_Generated_Image_k5gzztk5gzztk5gz.png"
                alt="Black Rubber Bush"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">Black Rubber Bush</p>
            </div>
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/j535ejmu_Gemini_Generated_Image_uqblw2uqblw2uqbl.png"
                alt="Premium PU Bush"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">Premium PU Bush</p>
            </div>
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/uwx09ueh_Gemini_Generated_Image_snhk5csnhk5csnhk.png"
                alt="Black Rubber Bush"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">Black Rubber Bush</p>
            </div>
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/e0vjte0q_Gemini_Generated_Image_4grzti4grzti4grz.png"
                alt="PU Bush"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">PU Bush</p>
            </div>
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/2p46im8z_Gemini_Generated_Image_uokno0uokno0uokn.png"
                alt="Complete Product Range"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">Complete Product Range</p>
            </div>
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/5jvalemy_Gemini_Generated_Image_qjik2mqjik2mqjik.png"
                alt="Internal Structure Detail"
                className="w-full h-56 object-cover rounded-xl shadow-lg"
              />
              <p className="text-center font-semibold text-gray-700">Internal Structure Detail</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-gradient-to-br from-amber-50 to-orange-50 px-8 py-6 rounded-2xl border-2 border-amber-200">
              <p className="text-2xl font-bold text-amber-800 mb-2">Custom Sizes Available</p>
              <p className="text-gray-700">Contact us for special requirements and bulk orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Page 12: Back Cover - Contact */}
      <div className="brochure-page cover-page bg-white">
        <div className="h-full p-16 flex flex-col justify-between">
          <div>
            <h2 className="text-5xl font-bold mb-3 text-gray-800">Get In Touch</h2>
            <div className="w-24 h-1 bg-amber-600 mb-12"></div>

            <div className="grid grid-cols-2 gap-12">
              {/* Contact Information */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4 bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                  <div className="bg-amber-600 p-3 rounded-lg flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-800">Phone</h4>
                    <p className="text-amber-800 font-semibold">+91 9824138001</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                  <div className="bg-green-600 p-3 rounded-lg flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-800">WhatsApp</h4>
                    <p className="text-green-700 font-semibold">+91 9824138001</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                  <div className="bg-amber-600 p-3 rounded-lg flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-800">Email</h4>
                    <p className="text-amber-800 text-sm break-all">camel.rubberindustries@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl border border-amber-200">
                  <div className="bg-amber-600 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-gray-800">Address</h4>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      14, Dhara Estate, Santej-Khatraj Road,<br/>
                      Santej, Ta.: Kalol, Gandhinagar,<br/>
                      Ahmedabad, Gujarat, India
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-amber-600 to-orange-600 text-white p-8 rounded-2xl shadow-xl">
                  <h3 className="text-3xl font-bold mb-6">Why Choose Us?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="text-amber-200 mr-3 text-xl flex-shrink-0">✓</span>
                      <span className="text-lg">40+ years of industry expertise</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-200 mr-3 text-xl flex-shrink-0">✓</span>
                      <span className="text-lg">Custom machinery design and innovation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-200 mr-3 text-xl flex-shrink-0">✓</span>
                      <span className="text-lg">Premium quality products</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-200 mr-3 text-xl flex-shrink-0">✓</span>
                      <span className="text-lg">Field-tested in extreme conditions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-200 mr-3 text-xl flex-shrink-0">✓</span>
                      <span className="text-lg">Custom solutions for unique needs</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl border-2 border-amber-300 text-center shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/yv3r7xql_Adobe%20Express%20-%20file.png"
                    alt="Camel Logo"
                    className="h-20 w-20 mx-auto mb-4"
                    style={{
                      filter: 'brightness(0) saturate(100%) invert(29%) sepia(74%) saturate(1447%) hue-rotate(2deg) brightness(94%) contrast(97%)'
                    }}
                  />
                  <h3 className="text-3xl font-bold mb-2 text-gray-800" style={{ fontFamily: "'Monotype Corsiva', cursive" }}>Camel</h3>
                  <p className="text-amber-700 font-semibold text-lg">Rubber Industries</p>
                  <p className="text-gray-600 text-sm mt-3">Est. 2012</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center border-t-2 border-amber-300 pt-8 mt-8">
            <p className="text-gray-700 font-semibold text-lg">© 2024 Camel Rubber Industries. All rights reserved.</p>
            <p className="text-amber-700 font-medium mt-2">Serving the industry with excellence since 2012</p>
          </div>
        </div>
      </div>

      <style>{`
        @page {
          size: A4;
          margin: 0;
        }
        
        .brochure-page {
          width: 210mm;
          height: 297mm;
          page-break-after: always;
          position: relative;
          background: white;
        }
        
        .brochure-page:last-child {
          page-break-after: auto;
        }
        
        @media print {
          /* Force exact color rendering - preserve ALL colors as they appear on screen */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          .brochure-page {
            margin: 0;
            page-break-after: always;
          }
          
          .brochure-page:last-child {
            page-break-after: auto;
          }
          
          /* Ensure images render properly */
          img {
            max-width: 100%;
            page-break-inside: avoid;
          }
        }
        
        @media screen {
          .brochure-container {
            max-width: 210mm;
            margin: 0 auto;
          }
          
          .brochure-page {
            margin-bottom: 20px;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </div>
  );
};

export default Brochure;
